import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toastOpen } from 'core/slices/toasts';
import { TOAST_FAIL } from 'components/Toaster/Toaster';
import '@ingka/text/dist/style.css';
import '@ingka/table/dist/style.css';
import './GridReportTable.scss';
import {
  ColumnDefinition,
  FetchParams,
  FetchResponse,
  KeyValuePair,
  SortModel,
} from '../../models';
import { LoadingOverlay } from './components/loading-overlay/LoadingOverlay';
import { loadData } from '../functions/loadData';
import { setupIntersectionObserver } from '../functions/setupIntersectionObserver';
import { createFilterChangeHandler } from '../functions/createFilterChangeHandler';
import { createSortToggleHandler } from '../functions/createSortToggleHandler';
import { GridReportSlicers } from './components/grid-report-slicers/GridReportSlicers';
import { GridReportTableBody } from './components/grid-report-table/GridReportTableBody';
import { isEqual } from 'lodash';
import { usePrevious } from 'hooks/usePrevious';

interface ServerTableProps {
  reportId: string;
  fetchData: (params: FetchParams) => Promise<FetchResponse>;
  pageSize: number;
  filterValues: KeyValuePair[];
  orderModel: SortModel[];
  onFilterChange?: (name: string, value: any) => void;
  onClearFilters?: () => void;
  countries: string[];
}

const GridReportTableC: React.FC<ServerTableProps> = ({
  reportId,
  fetchData,
  pageSize,
  filterValues,
  orderModel = [],
  onFilterChange,
  onClearFilters,
  countries,
}) => {
  const [data, setData] = useState<any[]>([]);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [sortModel, setSortModel] = useState<SortModel[]>(orderModel || []);
  const [columns, setColumns] = useState<ColumnDefinition[]>([]);
  // Pivot reports render server-generated columns; configured columns still drive the filters.
  const [pivotColumns, setPivotColumns] = useState<ColumnDefinition[] | null>(
    null
  );
  const [rowGroupFields, setRowGroupFields] = useState<string[]>([]);
  const [filterOptionValues, setFilterOptionValues] = useState<any>({});
  const loadingTriggerRef = useRef<HTMLTableRowElement>(null);
  const lastLoadTimeRef = useRef<number>(0);
  const prevFilterValues = usePrevious(filterValues);

  const storageKey = `grid-report-columns:${reportId}`;

  const getStoredColumns = (): string[] | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map(String) : null;
    } catch {
      return null;
    }
  };

  const applyStoredVisibility = (
    cols: ColumnDefinition[],
    stored: string[] | null
  ): ColumnDefinition[] => {
    return cols
      .map(col => ({
        ...col,
        systemHidden: col.show === false,
      }))
      .map(col => ({
        ...col,
        show: col.systemHidden
          ? false
          : stored
            ? stored.includes(String(col.field))
            : col.show !== false,
      }));
  };

  const loadDataInternal = useCallback(
    async (isLoadingMore = false) => {
      const filtersChanged = !isEqual(prevFilterValues, filterValues);
      if (filtersChanged || data.length === 0) {
        setLoading(true);
      }

      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setPage(1);
        setHasMoreData(true);
        setLoadingMore(false);
      }

      try {
        const currentPage = isLoadingMore ? page : 1;
        const result = await loadData({
          fetchData,
          pageSize,
          sortModel,
          filterValues,
          currentPage,
          isLoadingMore,
        });

        if (isLoadingMore) {
          // Append new data to existing data
          setData(prevData => {
            const newData = [...prevData, ...result.data];
            return newData;
          });
          setPage(result.nextPage);
        } else {
          // Replace data for initial load or after filter/sort change
          setData(result.data);
          setPage(result.nextPage);
        }

        setFilterOptionValues(result.filterOptionValues);
        const stored = getStoredColumns();
        setColumns(applyStoredVisibility(result.columns, stored));
        setPivotColumns(result.pivotColumns);
        setRowGroupFields(result.rowGroupFields);

        setHasMoreData(result.hasMoreData);
      } catch (error) {
        dispatch(
          toastOpen({
            className: TOAST_FAIL,
            text:
              'Failed to load data: ' +
              (error instanceof Error ? error.message : 'Unknown error'),
          })
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, pageSize, sortModel, filterValues]
  );

  // Load more data function with debounce protection
  const loadMore = useCallback(() => {
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;

    // Debounce: prevent loading more than once per 500ms
    if (timeSinceLastLoad < 500) {
      return;
    }

    if (!loadingMore && hasMoreData && !loading) {
      lastLoadTimeRef.current = now;
      loadDataInternal(true);
    }
  }, [loadingMore, hasMoreData, loading, loadDataInternal]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const cleanup = setupIntersectionObserver(
      loadingTriggerRef.current,
      hasMoreData,
      loading,
      loadingMore,
      loadMore
    );

    return cleanup;
  }, [hasMoreData, loadMore, loading, loadingMore]);

  useEffect(() => {
    loadDataInternal(false); // Initial load or after filter/sort change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortModel, filterValues]);

  const toggleSort = createSortToggleHandler(sortModel, setSortModel, setPage);

  const handleFilterChange = createFilterChangeHandler(setPage, onFilterChange);

  const handleClearFiltersExtended = () => {
    if (onClearFilters) onClearFilters();
    setSortModel([]);
  };

  const handleSaveColumnSelection = (selectedFields: string[]) => {
    const updated = columns.map(col => ({
      ...col,
      show: col.systemHidden
        ? false
        : selectedFields.includes(String(col.field)),
    }));
    setColumns(updated);
    localStorage.setItem(storageKey, JSON.stringify(selectedFields));
  };

  return (
    <div id="grid-report-table">
      <LoadingOverlay loading={loading} message="Loading data...">
        <GridReportSlicers
          filterValues={filterValues}
          filterOptionValues={filterOptionValues}
          columns={columns}
          onSaveColumnSelection={handleSaveColumnSelection}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFiltersExtended}
          countries={countries}
        />
        <GridReportTableBody
          columns={pivotColumns ?? columns}
          sortModel={sortModel}
          toggleSort={toggleSort}
          data={data}
          loadingTriggerRef={loadingTriggerRef}
          hasMoreData={hasMoreData}
          loadingMore={loadingMore}
          rowGroupFields={rowGroupFields}
        />
      </LoadingOverlay>
    </div>
  );
};

const arePropsEqual = (
  prevProps: ServerTableProps,
  nextProps: ServerTableProps
) => {
  return (
    prevProps.reportId === nextProps.reportId &&
    prevProps.pageSize === nextProps.pageSize &&
    JSON.stringify(prevProps.filterValues) ===
      JSON.stringify(nextProps.filterValues) &&
    JSON.stringify(prevProps.orderModel) ===
      JSON.stringify(nextProps.orderModel)
  );
};

export const GridReportTable = React.memo(GridReportTableC, arePropsEqual);
